// src/utils/reciboPdf.js
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
pdfMake.addVirtualFileSystem(pdfFonts)

import logoUrl from '@/assets/logo.png' // Vite/webpack resolverá la URL

const CURRENCY = 'USD'
const fmt = (n, c = CURRENCY) =>
  new Intl.NumberFormat('es-PR', { style: 'currency', currency: c })
    .format(Number(n) || 0)

const toNum = (x) => {
  const v = Number(x)
  return Number.isFinite(v) ? v : 0
}

const isValidNumber = (v) => typeof v === 'number' && Number.isFinite(v)

const toRate = (v) => {
  const t = Number(v)
  if (!Number.isFinite(t)) return 0
  return t <= 1 ? t : t / 100
}

async function urlToDataURL(url) {
  try {
    const res = await fetch(url, { cache: 'no-cache' })
    const blob = await res.blob()
    return await new Promise((resolve, reject) => {
      const r = new FileReader()
      r.onload = () => resolve(r.result)
      r.onerror = reject
      r.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

/**
 * Distribuye 'impuestosTotales' entre items proporcionalmente a su lineSub.
 * Garantiza cuadre exacto al centavo con “largest remainder”.
 * Retorna un arreglo de lineTax prorrateados.
 */
function prorratearImpuestosPorLineas(items, impuestosTotales) {
  // Filtrar solo productos que NO sean servicios para el cálculo de impuestos
  const itemsConImpuestos = items.filter(it => it.tipo !== 'servicio')
  const sumSub = itemsConImpuestos.reduce((s, it) => s + it.lineSub, 0)
  
  if (sumSub <= 0 || impuestosTotales <= 0) {
    return items.map(() => 0)
  }

  // Cálculo proporcional crudo en centavos solo para productos
  const rawCents = itemsConImpuestos.map(it => (impuestosTotales * (it.lineSub / sumSub)) * 100)

  // Parte entera en centavos + residuos
  const baseCents = rawCents.map(x => Math.floor(x))
  const residuos = rawCents.map((x, i) => ({ i, frac: x - baseCents[i] }))

  let asignados = baseCents.reduce((s, c) => s + c, 0)
  const objetivo = Math.round(impuestosTotales * 100)
  let faltan = objetivo - asignados

  // Ordenar por mayor residuo para repartir los centavos restantes
  residuos.sort((a, b) => b.frac - a.frac)
  let k = 0
  while (faltan > 0 && k < residuos.length) {
    baseCents[residuos[k].i] += 1
    faltan -= 1
    k += 1
  }

  // Mapear resultados de vuelta a todos los items originales
  const resultado = items.map(() => 0)
  let indexProductos = 0
  items.forEach((item, index) => {
    if (item.tipo !== 'servicio') {
      resultado[index] = baseCents[indexProductos] / 100
      indexProductos++
    }
    // Los servicios quedan en 0 (ya inicializados)
  })

  return resultado
}

/**
 * computeTotales(venta):
 * - Si los ítems traen tasa => calcula por línea (tasa real)
 * - Si NO traen tasa pero venta.impuestos/subtotal/total vienen válidos => PRORRATEA impuestos
 * - Si viene solo una parte, completa con cálculos coherentes
 */
function computeTotales(venta) {
  const currency = typeof venta.currency === 'string' && venta.currency ? venta.currency : 'USD'

  // Normalizar items
  const productos = (venta.productos || []).map(p => {
    const qty = Math.max(1, Math.floor(toNum(p.cantidad)))
    const precio = toNum(p.precio)
    // Los servicios NO tienen impuestos aplicados
    const esServicio = p.tipo === 'servicio'
    const r = esServicio ? 0 : toRate(p.impuesto ?? p.tax ?? 0) // tasa por línea (si existe y no es servicio)
    const lineSub = +(precio * qty).toFixed(2)
    // Si hay tasa por línea, se calcula impuesto por línea; si no, será 0 y quizá se prorratea luego
    const lineTaxCalc = +(lineSub * r).toFixed(2)
    return {
      ...p,
      cantidad: qty,
      precio,
      r,
      lineSub,
      lineTaxCalc,     // impuesto calculado por línea (si hay tasa)
      lineTaxFinal: 0, // se definirá más abajo (o con prorrateo)
      lineTotal: 0,    // se define al final
      taxSource: r > 0 ? 'rate' : 'none' // etiqueta para la UI
    }
  })

  const subCalc = productos.reduce((s, it) => s + it.lineSub, 0)

  // ¿Hay alguna línea con tasa?
  const hayTasas = productos.some(it => it.r > 0)

  // Totales pasados por el caller (POS / e‑commerce)
  const subProvided = isValidNumber(venta.subtotal) ? venta.subtotal : null
  const impProvided = isValidNumber(venta.impuestos) ? venta.impuestos : null
  const totProvided = isValidNumber(venta.total) ? venta.total : null

  let subtotal = subCalc
  let impuestos = 0
  let total = 0

  if (hayTasas) {
    // Caso A: hay tasas por línea -> usar cálculo por línea
    impuestos = productos.reduce((s, it) => s + it.lineTaxCalc, 0)
    impuestos = +impuestos.toFixed(2)
    subtotal = isValidNumber(subProvided) ? subProvided : subCalc
    total = isValidNumber(totProvided) ? totProvided : +(subtotal + impuestos).toFixed(2)

    // Setear impuestos finales por línea desde el cálculo nativo
    productos.forEach(it => {
      it.lineTaxFinal = it.lineTaxCalc
      it.lineTotal = +(it.lineSub + it.lineTaxFinal).toFixed(2)
      it.taxSource = 'rate'
    })
  } else if (isValidNumber(impProvided) && impProvided > 0) {
    // Caso B: no hay tasas por línea pero recibimos impuestos totales => PRORRATEAR
    subtotal = isValidNumber(subProvided) ? subProvided : subCalc
    // Si recibimos total y difiere del (subtotal + impuestos), preferimos el total provisto y
    // ajustamos impuestos para cuadrar con el total (defensivo ante redondeos).
    const impuestosCalculados = +(isValidNumber(totProvided)
      ? (totProvided - subtotal)
      : impProvided).toFixed(2)

    impuestos = impuestosCalculados < 0 ? 0 : impuestosCalculados
    total = isValidNumber(totProvided) ? totProvided : +(subtotal + impuestos).toFixed(2)

    // PRORRATEO de impuestos por renglón
    const prorr = prorratearImpuestosPorLineas(productos, impuestos)
    productos.forEach((it, idx) => {
      it.lineTaxFinal = prorr[idx]
      it.lineTotal = +(it.lineSub + it.lineTaxFinal).toFixed(2)
      it.taxSource = 'prorated'
    })
  } else {
    // Caso C: no hay tasas y no recibimos impuestos -> todo exento
    subtotal = isValidNumber(subProvided) ? subProvided : subCalc
    impuestos = 0
    total = isValidNumber(totProvided) ? totProvided : +(subtotal + impuestos).toFixed(2)
    productos.forEach(it => {
      it.lineTaxFinal = 0
      it.lineTotal = +(it.lineSub + it.lineTaxFinal).toFixed(2)
      it.taxSource = 'none'
    })
  }

  // Redondeos defensivos al final
  subtotal = +subtotal.toFixed(2)
  impuestos = +impuestos.toFixed(2)
  total = +total.toFixed(2)

  return { productos, subtotal, impuestos, total, currency }
}

function buildDoc(venta, logoDataUrl) {
  const tienda = {
    nombre   : venta.tienda?.nombre    || 'NAT Spa Wellness Center',
    direccion: venta.tienda?.direccion || 'Calle Betance #56',
    telefono : venta.tienda?.telefono  || '(787) 415-9799',
    email    : venta.tienda?.email     || 'natspawellnesscenter@yahoo.com',
    web      : venta.tienda?.web       || ''
  }

  // Manejar fecha correctamente - puede ser Timestamp de Firestore o string/Date
  let fecha;
  if (venta.fecha) {
    // Si es un Timestamp de Firestore
    if (venta.fecha.toDate && typeof venta.fecha.toDate === 'function') {
      fecha = venta.fecha.toDate();
    } 
    // Si tiene método toMillis (también Timestamp)
    else if (venta.fecha.toMillis && typeof venta.fecha.toMillis === 'function') {
      fecha = new Date(venta.fecha.toMillis());
    }
    // Si es string o número
    else {
      fecha = new Date(venta.fecha);
    }
  } else {
    fecha = new Date();
  }
  
  // Verificar que la fecha sea válida
  if (isNaN(fecha.getTime())) {
    console.warn('Fecha inválida detectada, usando fecha actual');
    fecha = new Date();
  }

  // Cálculo robusto de totales y prorrateo si aplica
  const { productos, subtotal, impuestos, total, currency } = computeTotales(venta)

  const content = []

  // header centrado + logo
  content.push(
    logoDataUrl
      ? { image: logoDataUrl, width: 140, alignment: 'center', margin: [0,0,0,16] }
      : { text: tienda.nombre, fontSize: 16, bold: true, alignment: 'center', margin: [0,0,0,16] },
    { text: tienda.nombre, fontSize: 14, bold: true, alignment: 'center', color: '#0f172a' },
    { text: tienda.direccion, alignment: 'center', color: '#64748b', fontSize: 9 },
    { text: tienda.telefono, alignment: 'center', color: '#64748b', fontSize: 9 },
    { text: tienda.email, alignment: 'center', color: '#64748b', fontSize: 9, margin: [0,0,0,16] }
  )

  // meta - RECIBO y detalles
  content.push({
    text: 'RECIBO',
    fontSize: 14,
    bold: true,
    alignment: 'left',
    color: '#0f172a',
    margin: [0, 0, 0, 4]
  })
  
  content.push({
    text: `Fecha: ${fecha.toLocaleDateString('es-PR')} ${fecha.toLocaleTimeString('es-PR')}`,
    fontSize: 10,
    color: '#64748b',
    margin: [0, 0, 0, 2]
  })
  
  content.push({
    columns: [
      { text: `N.°: ${venta.id || '—'}`, color: '#64748b', fontSize: 10 },
      { text: `Pago: ${venta.metodoPago || 'stripe'}`, alignment: 'right', color: '#64748b', fontSize: 10 }
    ],
    margin: [0, 0, 0, 16]
  })

  // items - formato como en la imagen
  productos.forEach((p, i) => {
    // Nombre del producto en una línea
    content.push({
      text: p.nombre.toUpperCase(),
      fontSize: 10,
      bold: true,
      color: '#0f172a',
      margin: [0, 0, 0, 2]
    })
    
    
    
    // Cantidad y precio en la línea derecha
    content.push({
      columns: [
        { text: '', width: '*' },
        { 
          text: `×${p.cantidad}`,
          width: 'auto',
          alignment: 'right',
          fontSize: 10,
          color: '#64748b'
        }
      ],
      margin: [0, 0, 0, 2]
    })
    
    // Precio total de la línea
    content.push({
      text: fmt(p.lineSub, currency),
      alignment: 'right',
      fontSize: 11,
      bold: true,
      color: '#0f172a',
      margin: [0, 0, 0, i !== productos.length - 1 ? 12 : 16]
    })
  })

  // totales - formato simplificado como en la imagen
  content.push({
    columns: [
      { text: 'Subtotal', width: '*', alignment: 'right', fontSize: 10, color: '#64748b' },
      { text: fmt(subtotal, currency), width: 'auto', alignment: 'right', fontSize: 10, color: '#0f172a' }
    ],
    margin: [0, 0, 0, 4]
  })
   // descuento (si aplica)
if (venta.descuento && Number(venta.descuento) > 0) {
  content.push({
    columns: [
      { text: 'Descuento', width: '*', alignment: 'right', fontSize: 10, color: '#64748b' },
      { text: `- ${fmt(venta.descuento, currency)}`, width: 'auto', alignment: 'right', fontSize: 10, color: '#0f172a' }
    ],
    margin: [0, 0, 0, 4]
  })
}
  content.push({
    columns: [
      { text: 'Impuestos', width: '*', alignment: 'right', fontSize: 10, color: '#64748b' },
      { text: fmt(impuestos, currency), width: 'auto', alignment: 'right', fontSize: 10, color: '#0f172a' }
    ],
    margin: [0, 0, 0, 4]
  })
  
  content.push({
    columns: [
      { text: 'TOTAL', width: '*', alignment: 'right', fontSize: 12, bold: true, color: '#0f172a' },
      { text: fmt(total, currency), width: 'auto', alignment: 'right', fontSize: 12, bold: true, color: '#0f172a' }
    ],
    margin: [0, 8, 0, 16]
  })

  // QR - formato como en la imagen
  content.push({
    columns: [
      { 
        qr: `RECIBO:${venta.id || 'N/A'}|EMAIL:${tienda.email}`, 
        fit: 80,
        margin: [0, 0, 16, 0]
      },
      { 
        width: '*', 
        stack: [
          { text: 'Escanea para referencia', fontSize: 9, color: '#64748b' },
          { text: tienda.email, fontSize: 9, color: '#64748b', margin: [0, 2, 0, 0] }
        ]
      }
    ],
    margin: [0, 16, 0, 0]
  })

  content.push({ 
    text: 'Gracias por su compra.', 
    alignment: 'center', 
    color: '#64748b', 
    fontSize: 9, 
    margin: [0, 20, 0, 0] 
  })

  return {
    pageSize: { width: 400, height: 'auto' }, // ancho “mobile-friendly”
    pageMargins: [20, 24, 20, 30],
    defaultStyle: { font: 'Roboto', fontSize: 10, color: '#111827' },
    content
  }
}

// 🚀 API: genera base64 directo (así lo mandas al email)
export async function generarPDFVentaBase64(venta) {
  const logoDataUrl = (await urlToDataURL(logoUrl)) || null
  const doc = buildDoc(venta, logoDataUrl)
  const pdf = pdfMake.createPdf(doc)
  const base64 = await new Promise((resolve, reject) => {
    try { pdf.getBase64((b64) => resolve(b64)) } catch (e) { reject(e) }
  })
  return base64
}

/* ===================== RECIBO DE CITA (DEPÓSITO 50%) ===================== */
export async function generarPDFCitaDepositoBase64({
  id = '',
  fecha = new Date().toISOString(),
  metodoPago = '',
  email = '',
  servicios = [],         // preferido
  productos = [],         // alternativo
  items = [],             // alternativo
  valorCita,              // opcional; si no, se calcula
  deposito,               // opcional; si no, 50% de valorCita
  balancePendiente,       // opcional; si no, valorCita - deposito
  tienda = {},            // { nombre, direccion, telefono, email }
} = {}) {
  const money = (n) => `$${Number(n || 0).toFixed(2)}`
  const toN  = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
  const loadDataUrl = async (url) => {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()
    return new Promise((resolve) => {
      const fr = new FileReader()
      fr.onload = () => resolve(fr.result)
      fr.readAsDataURL(blob)
    })
  }

  const fuenteCruda = (Array.isArray(servicios) && servicios.length)
    ? servicios
    : (Array.isArray(productos) && productos.length)
      ? productos
      : (Array.isArray(items) && items.length)
        ? items
        : []

  const pickName = (o) =>
    o?.nombre ?? o?.name ?? o?.titulo ?? o?.title ??
    o?.descripcion ?? o?.description ??
    o?.servicio?.nombre ?? o?.producto?.nombre ?? 'Servicio'

  const pickPrice = (o) =>
    toN(
      o?.precio ?? o?.price ?? o?.precioUnitario ?? o?.unitPrice ??
      o?.monto ?? o?.amount ?? o?.servicio?.precio ?? o?.producto?.precio ?? 0
    )

  const pickQty = (o) => {
    const q = Number(o?.cantidad ?? o?.qty ?? 1)
    return Number.isFinite(q) && q > 0 ? q : 1
  }

  const itemsNorm = fuenteCruda.map((o) => ({
    nombre: pickName(o),
    precio: pickPrice(o),
    cantidad: pickQty(o),
  }))

  const valorCitaCalc =
    Number.isFinite(valorCita) ? Number(valorCita)
    : itemsNorm.reduce((s, it) => s + (toN(it.precio) * pickQty(it)), 0)

  const depositoCalc =
    Number.isFinite(deposito) ? Number(deposito)
    : +(valorCitaCalc * 0.5).toFixed(2)

  const balanceCalc =
    Number.isFinite(balancePendiente) ? Number(balancePendiente)
    : Math.max(0, +(valorCitaCalc - depositoCalc).toFixed(2))

  let logoDataUrl = ''
  try {
    logoDataUrl = await loadDataUrl(logoUrl)
  } catch {
    logoDataUrl = ''
  }

  const itemsStack = (itemsNorm.length ? itemsNorm : [{ nombre: 'Servicio', precio: 0, cantidad: 1 }])
    .flatMap((it) => ([
      {
        columns: [
          { text: it.nombre, width: '*', fontSize: 10 },
          { text: `×${pickQty(it)}`, width: 40, alignment: 'right', color: '#64748b', fontSize: 10 },
          { text: money(toN(it.precio) * pickQty(it)), width: 70, alignment: 'right', fontSize: 10 },
        ],
        margin: [0, 4, 0, 2],
      },
      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 520, y2: 0, lineWidth: 0.5, lineColor: '#e5e7eb' }] },
    ]))

  const resumenDeposito = [
    {
      table: {
        widths: ['*', 'auto'],
        body: [
          [{ text: 'Valor de la cita', fontSize: 10, color: '#0f172a' }, { text: money(valorCitaCalc), fontSize: 10, alignment: 'right' }],
          [{ text: 'Depósito cobrado hoy', fontSize: 10, color: '#0f172a' }, { text: money(depositoCalc), fontSize: 11, alignment: 'right', bold: true }],
          [{ text: 'Saldo pendiente', fontSize: 10, color: '#0f172a' }, { text: money(balanceCalc), fontSize: 10, alignment: 'right' }],
        ],
      },
      layout: {
        fillColor: (row) => (row === 1 ? '#eef2ff' : null),
        hLineColor: '#e5e7eb',
        vLineColor: '#e5e7eb',
        paddingLeft: () => 8,
        paddingRight: () => 8,
        paddingTop: () => 6,
        paddingBottom: () => 6,
      },
      margin: [0, 8, 0, 12],
    },
  ]

  const totalesHoy = [
    {
      table: {
        widths: ['*', 140],
        body: [
          [{ text: 'Subtotal cobrado hoy', fontSize: 10, color: '#0f172a' }, { text: money(depositoCalc), fontSize: 10, alignment: 'right' }],
          [{ text: 'Impuestos', fontSize: 10, color: '#0f172a' }, { text: money(0), fontSize: 10, alignment: 'right' }],
          [{ text: 'TOTAL COBRADO HOY', fontSize: 10, bold: true, color: '#0f172a' }, { text: money(depositoCalc), fontSize: 11, bold: true, alignment: 'right' }],
        ],
      },
      layout: {
        fillColor: (row) => (row === 2 ? '#ecfeff' : null),
        hLineColor: '#e5e7eb',
        vLineColor: '#e5e7eb',
        paddingLeft: () => 8,
        paddingRight: () => 8,
        paddingTop: () => 6,
        paddingBottom: () => 6,
      },
    },
  ]

  // Manejar fecha correctamente - puede ser Timestamp de Firestore o string/Date
  let fechaFormateada;
  if (fecha) {
    let fechaObj;
    // Si es un Timestamp de Firestore
    if (fecha.toDate && typeof fecha.toDate === 'function') {
      fechaObj = fecha.toDate();
    } 
    // Si tiene método toMillis (también Timestamp)
    else if (fecha.toMillis && typeof fecha.toMillis === 'function') {
      fechaObj = new Date(fecha.toMillis());
    }
    // Si es string o número
    else {
      fechaObj = new Date(fecha);
    }
    
    // Verificar que la fecha sea válida
    if (isNaN(fechaObj.getTime())) {
      console.warn('Fecha inválida detectada, usando fecha actual');
      fechaObj = new Date();
    }
    
    fechaFormateada = fechaObj.toLocaleString('es-PR');
  } else {
    fechaFormateada = new Date().toLocaleString('es-PR');
  }

  const content = [
    ...(logoDataUrl ? [{ image: logoDataUrl, width: 140, alignment: 'center', margin: [0, 0, 0, 8] }] : []),
    { text: tienda.nombre || 'NAT Spa Wellness Center', alignment: 'center', bold: true, fontSize: 12 },
    { text: [tienda.direccion || 'Calle Betance #56', ' · ', tienda.telefono || '(787) 415-9799'].join(''), alignment: 'center', color: '#64748b', fontSize: 9 },
    { text: tienda.email || 'natspawellnesscenter@yahoo.com', alignment: 'center', color: '#64748b', fontSize: 9, margin: [0, 0, 0, 8] },

    {
      columns: [
        { text: 'RECIBO DE CITA (DEPÓSITO)', width: '*', bold: true, fontSize: 12 },
        { text: `No. ${id || '-'}`, width: 'auto', alignment: 'right', color: '#64748b', fontSize: 9 },
      ],
      margin: [0, 4, 0, 2],
    },
    { text: `Fecha: ${fechaFormateada}`, color: '#64748b', fontSize: 9 },
    { text: `Pago: ${metodoPago || '-'}`, color: '#64748b', fontSize: 9, margin: [0, 0, 0, 8] },

    ...itemsStack,
    ...resumenDeposito,
    ...totalesHoy,

    ...(email ? [{ text: `Enviado a: ${email}`, color: '#64748b', fontSize: 9, alignment: 'center', margin: [0, 8, 0, 0] }] : []),
    { text: 'Gracias por su preferencia.', color: '#64748b', fontSize: 9, alignment: 'center', margin: [0, 4, 0, 0] },
  ]

  const docDefinition = {
    pageSize: 'A5',
    pageMargins: [24, 24, 24, 28],
    content,
    defaultStyle: { font: 'Roboto' },
  }

  return new Promise((resolve) => {
    pdfMake.createPdf(docDefinition).getBase64((b64) => resolve(b64))
  })
}
